import { Component, Input, OnDestroy, OnInit } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { Candidate } from '../../../../../shared/models';
import { ProfileMessagesModalComponent } from '../profile-messages-modal.component';
import { CampaignRecipient } from '../../../../campaign/modules/campaign-analytics/shared/models';
import {
  MessageRecipientInfo,
  MessageSenderInfo,
  ProfileMessagesModalData,
} from '../../../models/profile-messages.model';
import { SafeResourceUrl } from '@angular/platform-browser';
import { AccountService, CampaignAccountsService } from '../../../../../shared/services';
import { LimitationService } from '../../../services/limitation.service';
import { takeUntil } from 'rxjs/operators';
import { BehaviorSubject, Subject, take } from 'rxjs';
import { CampaignService } from '../../../../../shared/services/requests/campaign.service';
import { CampaignLinkedinCredential } from '../../../../../shared/models/campaign/campaign-settings';
import {
  LimitationsModalData,
  SimpleLimitationModalComponent,
} from '../../simple-limitation-modal';
import { SystemPages } from '../../../../../shared/enums/routerPaths.enum';

@Component({
  selector: 'app-open-messages-modal-button',
  templateUrl: 'open-messages-modal-button.component.html',
  styleUrls: ['open-messages-modal-button.component.scss'],
  standalone: false,
})
export class OpenMessagesModalButtonComponent implements OnInit, OnDestroy {
  @Input() profile: Candidate | null = null;
  @Input() campaignRecipient: CampaignRecipient | null = null;
  @Input() profilePicture: SafeResourceUrl | null = null;
  @Input() type: 'button' | 'icon' = 'button';

  protected isAccountLimited = false;

  private unsubscribe$ = new Subject<void>();
  private disableButtonSrc$ = new BehaviorSubject<boolean>(false);

  protected readonly disableButton$ = this.disableButtonSrc$.asObservable();

  constructor(
    private dialog: MatDialog,
    private accountService: AccountService,
    private limitationService: LimitationService,
    private campaignService: CampaignService,
    private campaignAccountsService: CampaignAccountsService,
  ) {}

  ngOnInit(): void {
    this.accountService
      .isLimited$()
      .pipe(takeUntil(this.unsubscribe$))
      .subscribe((isLimited) => (this.isAccountLimited = isLimited));
  }

  ngOnDestroy(): void {
    this.unsubscribe$.next();
    this.unsubscribe$.complete();
  }

  onOpenMessagesModalClick(): void {
    if (this.isAccountLimited) {
      this.limitationService.onPlanLimit();
    } else {
      this.disableButton(true);
      this.campaignService
        .getLinkedinCredentials()
        .pipe(take(1))
        .subscribe({
          next: (accounts: CampaignLinkedinCredential[]) => {
            if (!accounts) {
              accounts = [];
            }
            const isActiveAccountIncluded = accounts.some((account) =>
              this.campaignAccountsService.canAccessAccount(account),
            );

            if (isActiveAccountIncluded) {
              this.openMessagesModal(accounts);
            } else {
              this.openNoAccountsModal();
            }
            this.disableButton(false);
          },
          error: () => {
            this.disableButton(false);
          },
        });
    }
  }

  private disableButton(value: boolean): void {
    this.disableButtonSrc$.next(value);
  }

  private openNoAccountsModal(): void {
    const translationKey = 'MESSAGES.NO_ACCOUNTS_MODAL';
    const data: LimitationsModalData = {
      title: `${translationKey}.TITLE`,
      description: `${translationKey}.DESCRIPTION`,
      action: {
        text: `${translationKey}.ADD_BUTTON`,
        routerLink: SystemPages.ACCOUNTS,
      },
    };

    this.dialog.open(SimpleLimitationModalComponent, { data });
  }

  private openMessagesModal(accounts: CampaignLinkedinCredential[]): void {
    const recipient: MessageRecipientInfo | null = this.generateMessageRecipientInfo();

    if (recipient) {
      const senders: MessageSenderInfo[] = this.generateMessageSenderInfoList(accounts);
      const data: ProfileMessagesModalData = { recipient, senders };

      this.dialog.open(ProfileMessagesModalComponent, { data });
    }
  }

  private generateMessageSenderInfoList(
    accounts: CampaignLinkedinCredential[],
  ): MessageSenderInfo[] {
    return accounts.map((item) => ({
      id: item.id,
      name: item.name,
      avatar: '',
      status: item.status,
      isAccessible: this.campaignAccountsService.canAccessAccount(item),
    }));
  }

  private generateMessageRecipientInfo(): MessageRecipientInfo | null {
    if (this.profile) {
      const { docId, fullName, pictureUrl } = this.profile;
      const avatar: string = pictureUrl || (this.profilePicture as string) || '';
      return {
        id: docId,
        name: fullName,
        avatar,
      };
    } else if (this.campaignRecipient) {
      const { docId, fullName } = this.campaignRecipient;
      return {
        id: docId,
        name: fullName,
        avatar: '',
      };
    }
    return null;
  }
}
